import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { AIModelConfig, AIProviderConfigUpdate } from '@/types';

interface ModelOption {
  label: string;
  id: string;
  group: string;
  tier?: string;
}

// Well-known models grouped by provider — serves as both suggestions and documentation
const WELL_KNOWN_MODELS: ModelOption[] = [
  // IONOS AI Model Hub — https://docs.ionos.com/cloud/ai/ai-model-hub/models
  { label: 'Llama 3.1 8B Instruct', id: 'meta-llama/Meta-Llama-3.1-8B-Instruct', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Qwen3.5 9B', id: 'Qwen/Qwen3.5-9B', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Mistral Nemo Instruct', id: 'mistralai/Mistral-Nemo-Instruct-2407', group: 'IONOS AI Model Hub', tier: 'standard' },
  { label: 'Mistral Small 24B Instruct', id: 'mistralai/Mistral-Small-24B-Instruct', group: 'IONOS AI Model Hub', tier: 'plus' },
  { label: 'GPT-OSS 120B', id: 'openai/gpt-oss-120b', group: 'IONOS AI Model Hub', tier: 'premium' },
  { label: 'Llama 3.3 70B Instruct', id: 'meta-llama/Llama-3.3-70B-Instruct', group: 'IONOS AI Model Hub', tier: 'premium' },
  { label: 'Llama 3.1 405B Instruct', id: 'meta-llama/Meta-Llama-3.1-405B-Instruct-FP8', group: 'IONOS AI Model Hub', tier: 'premium' },
  { label: 'Qwen3.5 397B A17B', id: 'Qwen/Qwen3.5-397B-A17B', group: 'IONOS AI Model Hub', tier: 'premium' },
  // Anthropic Claude — latest
  { label: 'Claude Opus 4.7', id: 'claude-opus-4-7', group: 'Anthropic Claude' },
  { label: 'Claude Sonnet 4.6', id: 'claude-sonnet-4-6', group: 'Anthropic Claude' },
  { label: 'Claude Haiku 4.5', id: 'claude-haiku-4-5-20251001', group: 'Anthropic Claude' },
  // Anthropic Claude — previous generation
  { label: 'Claude Sonnet 4.5', id: 'claude-sonnet-4-5-20250929', group: 'Anthropic Claude' },
  { label: 'Claude Opus 4.5', id: 'claude-opus-4-5-20251101', group: 'Anthropic Claude' },
  { label: 'Claude Sonnet 4', id: 'claude-sonnet-4-20250514', group: 'Anthropic Claude' },
  { label: 'Claude Opus 4', id: 'claude-opus-4-20250514', group: 'Anthropic Claude' },
  // OpenAI
  { label: 'GPT-4o', id: 'gpt-4o', group: 'OpenAI' },
  { label: 'GPT-4o Mini', id: 'gpt-4o-mini', group: 'OpenAI' },
  { label: 'GPT-4 Turbo', id: 'gpt-4-turbo', group: 'OpenAI' },
  { label: 'GPT-3.5 Turbo', id: 'gpt-3.5-turbo', group: 'OpenAI' },
];

interface AIProviderModelSubTabProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

export default function AIProviderModelSubTab({ teamId, onNotify }: AIProviderModelSubTabProps) {
  const queryClient = useQueryClient();

  // Provider config state
  const [providerForm, setProviderForm] = useState<AIProviderConfigUpdate>({});

  // Model config state
  const [modelForm, setModelForm] = useState<Partial<AIModelConfig>>({});

  // Change tracking
  const [hasChanges, setHasChanges] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ status: string; message: string } | null>(null);

  const { data: providerConfig } = useQuery({
    queryKey: ['team-ai-provider-config', teamId],
    queryFn: () => teamApi.getAIProviderConfig(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: aiConfig } = useQuery({
    queryKey: ['team-ai-config', teamId],
    queryFn: () => teamApi.getAIConfig(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: availableModels } = useQuery({
    queryKey: ['available-models'],
    queryFn: () => teamApi.getAvailableModels().then((res) => res.data),
  });

  // Build model suggestions filtered by selected provider
  const modelOptions = useMemo(() => {
    const provider = providerForm.provider_type;
    // Filter well-known models by provider
    const visibleGroups =
      provider === 'anthropic'
        ? ['Anthropic Claude']
        : provider === 'openai_compatible'
          ? ['IONOS AI Model Hub', 'OpenAI']
          : ['IONOS AI Model Hub']; // global default = IONOS

    const options = WELL_KNOWN_MODELS.filter((m) => visibleGroups.includes(m.group));

    // Add DB models that aren't already in the list
    if (availableModels && visibleGroups.includes('IONOS AI Model Hub')) {
      for (const model of availableModels) {
        if (!options.some((o) => o.id === model.model_id)) {
          options.push({
            label: model.model_name,
            id: model.model_id,
            group: 'IONOS AI Model Hub',
            tier: model.tier,
          });
        }
      }
    }
    return options;
  }, [availableModels, providerForm.provider_type]);

  // Controlled input for the model Autocomplete
  const [modelInputValue, setModelInputValue] = useState('');

  useEffect(() => {
    if (providerConfig) {
      setProviderForm({
        provider_type: providerConfig.provider_type,
        api_endpoint: providerConfig.api_endpoint,
        model_override: providerConfig.model_override,
      });
      if (providerConfig.model_override) {
        setModelInputValue(providerConfig.model_override);
      }
      setHasChanges(false);
      setTestResult(null);
    }
  }, [providerConfig]);

  useEffect(() => {
    if (aiConfig) {
      setModelForm({
        model_id: aiConfig.model_id,
        model_name: aiConfig.model_name,
        temperature: aiConfig.temperature,
        max_tokens: aiConfig.max_tokens,
        top_p: aiConfig.top_p,
      });
      // Only set model input if providerConfig hasn't already set a model_override
      if (aiConfig.model_id && !providerConfig?.model_override) {
        setModelInputValue(aiConfig.model_id);
      }
      setHasChanges(false);
    }
  }, [aiConfig, providerConfig?.model_override]);

  const updateProviderMutation = useMutation({
    mutationFn: (data: AIProviderConfigUpdate) => teamApi.updateAIProviderConfig(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-ai-provider-config', teamId] });
    },
    onError: (error: Error) => {
      onNotify(`Failed to save provider config: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateModelMutation = useMutation({
    mutationFn: (data: Partial<AIModelConfig>) => teamApi.updateAIConfig(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-ai-config', teamId] });
    },
    onError: (error: Error) => {
      onNotify(`Failed to save model config: ${getErrorMessage(error)}`, 'error');
    },
  });

  const clearProviderMutation = useMutation({
    mutationFn: () => teamApi.clearAIProviderConfig(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-ai-provider-config', teamId] });
      setProviderForm({});
      onNotify('Reverted to global AI settings', 'success');
      setHasChanges(false);
    },
    onError: (error: Error) => {
      onNotify(`Failed to clear: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleSaveAll = async () => {
    try {
      await Promise.all([
        updateProviderMutation.mutateAsync(providerForm),
        updateModelMutation.mutateAsync(modelForm),
      ]);
      onNotify('AI configuration saved successfully!', 'success');
      setHasChanges(false);
    } catch {
      // Individual error handlers will fire
    }
  };

  const handleProviderChange = (field: keyof AIProviderConfigUpdate, value: string | null) => {
    setProviderForm((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setTestResult(null);
  };

  const handleModelChange = (field: keyof AIModelConfig, value: unknown) => {
    setModelForm((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const res = await teamApi.testAIProviderConfig(teamId, providerForm);
      setTestResult(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setTestResult({ status: 'error', message });
    } finally {
      setTestingConnection(false);
    }
  };

  const isSaving = updateProviderMutation.isPending || updateModelMutation.isPending;

  return (
    <>
      {hasChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes.
        </Alert>
      )}

      {/* Provider Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                AI Provider
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose your team's AI provider. Leave empty to use the global default (IONOS).
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => clearProviderMutation.mutate()}
              disabled={clearProviderMutation.isPending || !providerConfig?.provider_type}
            >
              Reset to Global
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Provider Type</InputLabel>
                <Select
                  value={providerForm.provider_type || ''}
                  label="Provider Type"
                  onChange={(e) => {
                    const val = e.target.value || null;
                    if (val === 'anthropic') {
                      setProviderForm((prev) => ({ ...prev, provider_type: val, api_endpoint: null }));
                    } else {
                      setProviderForm((prev) => ({ ...prev, provider_type: val }));
                    }
                    setHasChanges(true);
                    setTestResult(null);
                  }}
                >
                  <MenuItem value="">
                    <em>Use Global Default (IONOS)</em>
                  </MenuItem>
                  <MenuItem value="openai_compatible">OpenAI Compatible (IONOS / OpenAI)</MenuItem>
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {providerForm.provider_type === 'openai_compatible' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="API Endpoint"
                  fullWidth
                  value={providerForm.api_endpoint || ''}
                  onChange={(e) => handleProviderChange('api_endpoint', e.target.value || null)}
                  helperText="OpenAI-compatible API endpoint URL. Leave empty for global default."
                  placeholder="https://openai.inference.de-txl.ionos.com/v1"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label="API Key"
                fullWidth
                type="password"
                value={providerForm.api_key || ''}
                onChange={(e) => handleProviderChange('api_key', e.target.value || null)}
                helperText={
                  providerConfig?.has_api_key
                    ? 'A key is stored. Enter a new one to replace it, or leave empty to keep current.'
                    : 'Team-specific API key (optional). Leave empty to use global key.'
                }
                placeholder={providerConfig?.has_api_key ? '••••••••' : 'Enter API key'}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={testingConnection || !providerForm.provider_type}
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </Button>
            {testResult && (
              <Alert severity={testResult.status === 'success' ? 'success' : 'error'} sx={{ flex: 1 }}>
                {testResult.message}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Model Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Model Selection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Type a model identifier or pick from the suggestions below. You can use any model
            supported by your selected provider.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete<ModelOption, false, false, true>
                freeSolo
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                options={modelOptions}
                groupBy={(option) =>
                  typeof option === 'string' ? '' : option.group
                }
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.id
                }
                inputValue={modelInputValue}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setModelInputValue(value);
                    setProviderForm((prev) => ({ ...prev, model_override: value || null }));
                    setHasChanges(true);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    setModelInputValue(newValue.id);
                    setProviderForm((prev) => ({ ...prev, model_override: newValue.id }));
                    setModelForm((prev) => ({
                      ...prev,
                      model_id: newValue.id,
                      model_name: newValue.label,
                    }));
                    setHasChanges(true);
                  } else if (newValue === null) {
                    setModelInputValue('');
                    setProviderForm((prev) => ({ ...prev, model_override: null }));
                    setHasChanges(true);
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  const filter = inputValue.toLowerCase();
                  if (!filter) return options;
                  return options.filter(
                    (o) =>
                      o.id.toLowerCase().includes(filter) ||
                      o.label.toLowerCase().includes(filter)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Model Identifier"
                    placeholder="e.g. meta-llama/Llama-3.3-70B-Instruct"
                    helperText="Start typing to see suggestions, or enter any model ID"
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                  return (
                    <li key={key} {...rest}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.id}
                          </Typography>
                        </Box>
                        {option.tier && (
                          <Chip
                            label={option.tier}
                            size="small"
                            color={
                              option.tier === 'premium'
                                ? 'error'
                                : option.tier === 'plus'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ ml: 1, textTransform: 'capitalize' }}
                          />
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Typography
                      variant="overline"
                      sx={{
                        px: 2,
                        pt: 1.5,
                        pb: 0.5,
                        display: 'block',
                        color: 'text.secondary',
                        fontWeight: 700,
                      }}
                    >
                      {params.group}
                    </Typography>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Model Display Name"
                fullWidth
                value={modelForm.model_name || ''}
                onChange={(e) => handleModelChange('model_name', e.target.value)}
                helperText="Friendly name shown in UI (can be customized)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Model Parameters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Model Parameters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fine-tune the AI model behavior for your team's responses.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Temperature: {modelForm.temperature?.toFixed(2) || '0.70'}</Typography>
              <Slider
                value={modelForm.temperature || 0.7}
                onChange={(_, value) => handleModelChange('temperature', value)}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Lower = more focused, Higher = more creative
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Max Tokens: {modelForm.max_tokens || 2048}</Typography>
              <Slider
                value={modelForm.max_tokens || 2048}
                onChange={(_, value) => handleModelChange('max_tokens', value)}
                min={256}
                max={8192}
                step={256}
                marks={[
                  { value: 256, label: '256' },
                  { value: 4096, label: '4K' },
                  { value: 8192, label: '8K' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Maximum length of AI response
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography gutterBottom>Top P: {modelForm.top_p?.toFixed(2) || '0.90'}</Typography>
              <Slider
                value={modelForm.top_p || 0.9}
                onChange={(_, value) => handleModelChange('top_p', value)}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Nucleus sampling threshold
              </Typography>
            </Grid>
          </Grid>

          {aiConfig && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date(aiConfig.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Save All */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSaveAll}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>
    </>
  );
}
